import {
	getLastElementIndexByQueuePriority,
	getLastElementIndexByStartDate,
	getLastElementIndexForParkedCalls,
} from "@/common/helpers/queueInteractionHelper";
import { InteractionType } from "@/common/types";
import {
	InteractionDirection,
	InteractionDto,
	InteractionOperations,
	InteractionStatus,
} from "@/repository/models";
import { useCallback, useMemo } from "react";

export const useInteractionsHandler = () => {
	const waitingStatuses: InteractionStatus[] = [
		InteractionStatus.Waiting,
		InteractionStatus.Offering,
		InteractionStatus.ColdTransferRinging,
		InteractionStatus.WarmTransferRinging,
		InteractionStatus.Parked,
		InteractionStatus.CallbackRequest,
		InteractionStatus.CallbackOffering,
	];

	const processAddedInteraction = useCallback(
		(
			interaction: InteractionDto,
			interactionsToProcess: InteractionDto[] | undefined
		) => {
			const processedInteractions = interactionsToProcess
				? [...interactionsToProcess]
				: [];
			const index = processedInteractions.findIndex(
				(i) => i.id === interaction.id
			);

			if (index === -1) {
				if (
					!waitingStatuses.includes(interaction.status) ||
					interaction.direction === InteractionDirection.Outbound
				) {
					processedInteractions.unshift(interaction);
				} else {
					const indexToInsert = getIndexToInsert(
						processedInteractions,
						interaction
					);
					processedInteractions.splice(indexToInsert, 0, interaction);
				}
			}
			// When the id is already present the Added is dropped: an existing row must
			// not be overwritten by a late/duplicate Added, which could revert fields a
			// newer Updated set in between (e.g. isLastMessageSeen). Handlers that need an
			// Added to refresh an existing row (parked re-offer, pickup offering, callback
			// requeue) convert it to Updated before calling processInteractionsChange.

			return processedInteractions;
		},
		[]
	);

	const getIndexToInsert = (
		processedInteractions: InteractionDto[],
		interaction: InteractionDto
	) => {
		if (interaction.status === InteractionStatus.Parked) {
			return getLastElementIndexForParkedCalls(processedInteractions);
		}

		if (
			interaction.type !== InteractionType.Voicemail &&
			interaction.status !== InteractionStatus.CallbackRequest
		) {
			return getLastElementIndexByQueuePriority(
				processedInteractions,
				interaction
			);
		}

		return getLastElementIndexByStartDate(processedInteractions, interaction);
	};

	const processUpdatedInteraction = useCallback(
		(
			interaction: InteractionDto,
			interactionsToProcess: InteractionDto[] | undefined
		) =>
			(interactionsToProcess || []).map((x) =>
				x.id === interaction.id ? { ...interaction } : x
			),
		[]
	);

	const processRemovedInteraction = useCallback(
		(
			interaction: InteractionDto,
			interactionsToProcess: InteractionDto[] | undefined
		) => {
			if (!interactionsToProcess) {
				return;
			}

			return interactionsToProcess.filter((i) => i.id !== interaction.id);
		},
		[]
	);

	const interactionActions = useMemo(
		() => ({
			[InteractionOperations.Added]: processAddedInteraction,
			[InteractionOperations.Updated]: processUpdatedInteraction,
			[InteractionOperations.Removed]: processRemovedInteraction,
		}),
		[
			processAddedInteraction,
			processUpdatedInteraction,
			processRemovedInteraction,
		]
	);

	const processInteractionsChange = useCallback(
		(interaction: InteractionDto, interactionsToProcess?: InteractionDto[]) =>
			interactionActions[interaction?.changeType]?.(
				interaction,
				interactionsToProcess
			),
		[interactionActions]
	);

	return { processInteractionsChange };
};
